import { designVariables } from "@studio3t/design-variables";
import {
  ButtonMediumPrimaryLeadingIcon,
  ButtonSmallSubtle,
  Icon,
  IconSVG,
} from "@studio3t/generic-components";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "styled-components";
import { scriptsApi } from "@/api/client";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import {
  BreadcrumbCurrent,
  BreadcrumbCurrentGroup,
  BreadcrumbLink,
  ListPageHeader,
} from "@/components/ListPageHeader";
import { ServiceStatusBadge } from "@/components/ServiceStatusBadge";
import {
  SkeletonBar,
  SkeletonBlock,
  SkeletonPill,
} from "@/components/Skeleton";
import { DEFAULT_INGEST_PORT } from "@/constants";
import {
  type QueryPhase,
  queryPhase,
  useJobs,
  useServiceResources,
  useServices,
  useTokens,
} from "@/hooks/useApi";
import { isLlmService } from "@/pages/Services/helpers";
import type { Service, TokenSummary } from "@/types/api";
import { DetailsSection } from "./DetailsSection";
import {
  buildJobStatusMap,
  findJobsUsingService,
  getProviderIcon,
  getProviderLabel,
  maskConnectionString,
} from "./helpers";
import { IngestPipelinesSection } from "./IngestSnippetSection";
import { countLlms, liveState } from "./llmDetail";
import {
  hasReplicationSlots,
  ReplicationSlotsSection,
} from "./ReplicationSlotsSection";
import { ScriptsSection } from "./ScriptsSection";
import { CrumbLeadingIcon, EmptyState, PageSkeletonStack } from "./styles";
import { TokensSection } from "./TokensSection";
import { UsageTabs } from "./UsageTabs";
import { UsedByPipelinesTable } from "./UsedByPipelinesTable";
import { useLlmDetailActions } from "./useLlmDetailActions";

// A linking page (e.g. a pipeline node) can pass where the back button should
// go via router state; otherwise fall back to the Services list.
function resolveBack(state: unknown): { label: string; path: string } {
  const from = (state as { from?: { pathname: string; label: string } } | null)
    ?.from;
  return {
    label: from?.label ?? "Services",
    path: from?.pathname ?? "/services",
  };
}

// The breadcrumb's live/idle badge, keyed to the jobs query's lifecycle:
// skeleton while it loads (no false "idle" flash), nothing if it failed — the
// verdict is unknown and a skeleton would promise progress that isn't coming.
function LiveBadge({ live, phase }: { live: boolean; phase: QueryPhase }) {
  if (phase === "loading") return <SkeletonPill aria-hidden="true" />;
  if (phase === "error") return null;
  return <ServiceStatusBadge live={live} />;
}

function ServiceDetailHeader({
  service,
  serviceCount,
  live,
  livePhase,
  onNavigateList,
  onAddService,
}: {
  service: Service;
  serviceCount: number | undefined;
  live: boolean;
  livePhase: QueryPhase;
  onNavigateList: () => void;
  onAddService: () => void;
}) {
  const theme = useTheme();
  return (
    <ListPageHeader
      title="Services"
      count={serviceCount}
      action={
        <ButtonMediumPrimaryLeadingIcon
          text="Add service"
          icon={Icon.Plus}
          onClick={onAddService}
        />
      }
      breadcrumb={
        <>
          <BreadcrumbLink text="Services list" onClick={onNavigateList} />
          {/* $wrap lets the status badge drop below a long service name on a
              narrow header, mirroring the pipeline-detail breadcrumb. */}
          <BreadcrumbCurrentGroup>
            <BreadcrumbCurrent>
              {/* Muted like every icon on a non-clickable crumb (the
                  pipeline crumb's rule): the color says it isn't a link. */}
              <CrumbLeadingIcon>
                <IconSVG
                  icon={getProviderIcon(service.provider)}
                  size={designVariables.ui.breadcrumbs.item.icon}
                  colors={{ default: theme.buttonSubtleDisabledText }}
                />
              </CrumbLeadingIcon>
              {service.name}
            </BreadcrumbCurrent>
            <LiveBadge live={live} phase={livePhase} />
          </BreadcrumbCurrentGroup>
        </>
      }
    />
  );
}

function ProviderInfoSection({
  service,
  resourcesData,
  jobs,
  jobsPhase,
  jobStatusMap,
  usedByPipelines,
  tokens,
  onCopy,
  onJobClick,
}: {
  service: Service;
  resourcesData: import("@/types/api").ServiceResourcesResponse | undefined;
  jobs: import("@/types/api").JobMetadata[] | undefined;
  // Lifecycle of the jobs query — the used-by table shows skeleton rows while
  // it loads and a failure message if it errors, instead of an incomplete
  // (empty) pipeline list or a forever-skeleton.
  jobsPhase: QueryPhase;
  jobStatusMap: Record<string, boolean>;
  usedByPipelines: import("@/types/api").JobMetadata[];
  tokens: TokenSummary[] | undefined;
  onCopy: (text: string) => void;
  onJobClick: (jobName: string) => void;
}) {
  if (service.provider === "http_source") {
    const routes =
      resourcesData?.resources
        ?.filter((r) => r.resource_type === "route")
        .map((r) => r.name) ?? [];
    return (
      <>
        <IngestPipelinesSection
          serviceName={service.name}
          port={service.port ?? DEFAULT_INGEST_PORT}
          jobs={jobs}
          jobStatusMap={jobStatusMap}
          tokens={tokens}
          routes={routes}
          onCopy={onCopy}
          onJobClick={onJobClick}
        />
        <TokensSection serviceName={service.name} routes={routes} />
      </>
    );
  }
  // LLM services are consumed by the Agent and AI Helper, not by pipelines
  // (the engine's `used_by_jobs` is always empty for them), so there is no
  // usage table to show.
  if (isLlmService(service)) return null;
  return (
    <>
      <UsedByPipelinesTable
        pipelines={usedByPipelines}
        phase={jobsPhase}
        onRowClick={onJobClick}
      />
      {hasReplicationSlots(service.provider) && (
        <ReplicationSlotsSection
          service={service}
          jobs={jobs}
          onJobClick={onJobClick}
        />
      )}
    </>
  );
}

function useServiceTokens(
  provider: Service["provider"] | undefined,
  name: string | undefined,
) {
  const key = provider === "http_source" ? (name ?? null) : null;
  return useTokens(key);
}

// Only UDF services with at least one script source get a Scripts section.
function hasScriptSources(
  service: Service,
): service is Service & { sources: NonNullable<Service["sources"]> } {
  return service.provider === "udf" && (service.sources?.length ?? 0) > 0;
}

function ServiceScriptCards({
  service,
  refetchServices,
}: {
  service: Service & { sources: NonNullable<Service["sources"]> };
  refetchServices: () => void;
}) {
  // The bin arms nothing inline — deletion is confirmed through the shared
  // modal, like the service/schema deletes.
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const confirmDelete = async () => {
    if (pendingDelete === null) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await scriptsApi.delete(service.name, pendingDelete);
      setPendingDelete(null);
      refetchServices();
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : "Failed to delete the script",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <ScriptsSection
        sources={service.sources}
        onCopy={(text) => navigator.clipboard.writeText(text).catch(() => {})}
        onDelete={setPendingDelete}
      />
      <ConfirmDeleteModal
        isOpen={pendingDelete !== null}
        title="Delete script"
        message={
          <>
            Are you sure you want to delete <strong>{pendingDelete}</strong>?
            This action cannot be undone.
          </>
        }
        loading={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
      />
    </>
  );
}

// Derives everything the page shows about the pipelines using this service:
// per-job running states, the full JobMetadata rows for the "Used by pipelines"
// table, and the live/idle verdict.
function useServiceUsage(
  jobs: import("@/types/api").JobMetadata[] | undefined,
  name: string | undefined,
  service: Service | undefined,
) {
  const jobStatusMap = useMemo(() => buildJobStatusMap(jobs), [jobs]);

  const usedByJobs = useMemo(
    () => findJobsUsingService(jobs, name, service, jobStatusMap),
    [jobs, name, service, jobStatusMap],
  );

  // The full JobMetadata for each using-pipeline (source/sinks/state), for the
  // "Used by pipelines" table. Names in used_by_jobs with no loaded job are
  // dropped — there's no pipeline data to render for them.
  const usedByPipelines = useMemo(() => {
    const byName = new Map((jobs ?? []).map((j) => [j.name, j]));
    return usedByJobs
      .map((u) => byName.get(u.name))
      .filter((j): j is import("@/types/api").JobMetadata => j != null);
  }, [jobs, usedByJobs]);

  // A service is "live" when a pipeline using it is currently running — the
  // same rule the Services list applies (see isLive in ServicesTable). LLMs
  // get their own verdict in liveState.
  const live = useMemo(() => usedByJobs.some((j) => j.running), [usedByJobs]);

  return { jobStatusMap, usedByPipelines, live };
}

// Loads the queries behind the page and derives the render-ready values, so
// the component below stays a thin gate-and-render shell (mirroring
// useServicesData on the Services list).
function useServiceDetailData(name: string | undefined) {
  const {
    data: services,
    isLoading: servicesLoading,
    refetch: refetchServices,
  } = useServices();
  const { data: jobs, error: jobsError } = useJobs();
  const { data: resourcesData, isLoading: resourcesLoading } =
    useServiceResources(name ?? "");

  const service = useMemo(
    () => services?.find((s) => s.name === name),
    [services, name],
  );
  const { data: tokens } = useServiceTokens(service?.provider, name);
  const usage = useServiceUsage(jobs ?? undefined, name, service);
  // Lifecycle of the jobs query behind the live badge and used-by table —
  // "error" stops their skeletons from spinning forever if the query fails.
  const jobsPhase = queryPhase(jobs, jobsError);

  return {
    ...usage,
    // The badge's verdict and its own lifecycle (an LLM's comes with the
    // service itself, not the jobs query).
    ...liveState(service, usage.live, jobsPhase),
    service,
    serviceCount: services?.length,
    llmCount: countLlms(services),
    refetchServices,
    // The first services load gates the whole page behind a skeleton.
    pageLoading: servicesLoading && !services,
    jobs: jobs ?? undefined,
    jobsPhase,
    resourcesData: resourcesData ?? undefined,
    resourcesLoading,
    tokens: tokens ?? undefined,
  };
}

function ServiceDetail() {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { label: backLabel, path: backPath } = resolveBack(location.state);

  const data = useServiceDetailData(name);

  const handleBack = useCallback(() => {
    navigate(backPath);
  }, [navigate, backPath]);

  const handleNavigateList = useCallback(() => {
    navigate("/services");
  }, [navigate]);

  const handleAddService = useCallback(() => {
    navigate("/services/new");
  }, [navigate]);

  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  }, []);

  const handleJobClick = useCallback(
    (jobName: string) => {
      navigate(`/pipelines/${encodeURIComponent(jobName)}`);
    },
    [navigate],
  );

  const llmActions = useLlmDetailActions(data.service, data.llmCount);

  if (data.pageLoading) {
    // Blocks approximate the real layout (breadcrumb row, details card, used-by
    // table) so the page doesn't jump when the content arrives.
    return (
      <PageSkeletonStack aria-hidden="true">
        <SkeletonBar $width="280px" $height="20px" />
        <SkeletonBlock $height="260px" />
        <SkeletonBlock $height="220px" />
      </PageSkeletonStack>
    );
  }

  if (!data.service) {
    return (
      <EmptyState>
        <p>Service "{name}" not found</p>
        <ButtonSmallSubtle text={`Back to ${backLabel}`} onClick={handleBack} />
      </EmptyState>
    );
  }

  return (
    <div>
      <ServiceDetailHeader
        service={data.service}
        serviceCount={data.serviceCount}
        live={data.live}
        livePhase={data.livePhase}
        onNavigateList={handleNavigateList}
        onAddService={handleAddService}
      />

      <DetailsSection
        service={data.service}
        resources={data.resourcesData?.resources}
        // Resources arrive from their own (potentially slow — it connects to
        // the live database) query; skeleton the row instead of popping it in.
        resourcesLoading={data.resourcesLoading}
        getProviderLabel={getProviderLabel}
        maskConnectionString={maskConnectionString}
        llm={llmActions.llm}
      />
      {llmActions.deleteModal}

      {hasScriptSources(data.service) ? (
        // Both pipeline usage and script sources: one tab structure
        // (Figma 13984-223188) instead of two stacked sections.
        <UsageTabs
          pipelinesBody={
            <UsedByPipelinesTable
              bare
              pipelines={data.usedByPipelines}
              phase={data.jobsPhase}
              onRowClick={handleJobClick}
            />
          }
          scriptsBody={
            <ServiceScriptCards
              service={data.service}
              refetchServices={data.refetchServices}
            />
          }
        />
      ) : (
        <ProviderInfoSection
          service={data.service}
          resourcesData={data.resourcesData}
          jobs={data.jobs}
          // Used-by rows derive from the jobs query; skeleton while it loads,
          // failure message if it errors.
          jobsPhase={data.jobsPhase}
          jobStatusMap={data.jobStatusMap}
          usedByPipelines={data.usedByPipelines}
          tokens={data.tokens}
          onCopy={handleCopy}
          onJobClick={handleJobClick}
        />
      )}
    </div>
  );
}

export default ServiceDetail;
